import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  Link,
  Grid
} from '@mui/material';
import {
  Close,
  Agriculture,
  ShoppingCart,
  Email,
  Lock,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import '../styles/Loader.css';

const LoginModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('choice'); // 'choice', 'farmer', 'buyer'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    showPassword: false
  });

  const handleClose = () => {
    setStep('choice');
    setError('');
    setFormData({
      email: '',
      password: '',
      rememberMe: false,
      showPassword: false
    });
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleTogglePasswordVisibility = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword
    });
  };

  const handleLogin = async (userType) => {
    setError('');

    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      setIsLoading(true);
      
      // Connexion avec Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;
      
      // Vérifier l'email
      if (!user.emailVerified) {
        await signOut(auth);
        setIsLoading(false);
        setError("Veuillez vérifier votre email avant de vous connecter");
        return;
      }

      // Logique stricte de validation des rôles
      if (userType === 'farmer') {
        // Vérifier si l'utilisateur est bien un agriculteur
        const farmerDoc = await getDoc(doc(db, "farmers", user.uid));
        
        if (!farmerDoc.exists()) {
          await signOut(auth);
          setIsLoading(false);
          setError("Ce compte n'est pas enregistré comme vendeur. Veuillez créer un compte vendeur ou vous connecter en tant qu'acheteur.");
          return;
        }

        const farmerData = farmerDoc.data();
        
        // Vérifier l'approbation admin pour les farmers
        if (!farmerData.adminApproved) {
          await signOut(auth);
          setIsLoading(false);
          setError("Votre compte vendeur est en attente de validation par un administrateur");
          return;
        }

        // Rediriger vers le dashboard farmer
        navigate('/farmer-dashboard');
        
      } else if (userType === 'buyer') {
        // Vérifier si l'utilisateur est bien un acheteur
        const buyerDoc = await getDoc(doc(db, "buyers", user.uid));
        
        if (!buyerDoc.exists()) {
          await signOut(auth);
          setIsLoading(false);
          setError("Ce compte n'est pas enregistré comme acheteur. Veuillez créer un compte acheteur ou vous connecter en tant que vendeur.");
          return;
        }

        // Rediriger vers la page sales pour voir tous les produits
        navigate('/sales');
      }

      setIsLoading(false);
      handleClose();
      
    } catch (error) {
      setIsLoading(false);
      let errorMessage = 'Une erreur est survenue lors de la connexion';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives échouées. Veuillez réessayer plus tard';
      }
      
      setError(errorMessage);
      console.error("Erreur de connexion:", error);
    }
  };

  const renderChoiceStep = () => (
    <Box>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#014527', fontWeight: 'bold' }}>
          Choisissez votre type de compte
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                },
                border: '2px solid transparent',
                '&:hover': {
                  borderColor: '#28a745'
                }
              }}
              onClick={() => setStep('farmer')}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Agriculture sx={{ fontSize: 60, color: '#28a745', mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Vendeur / Agriculteur
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connectez-vous pour gérer vos produits et vos ventes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                },
                border: '2px solid transparent',
                '&:hover': {
                  borderColor: '#007bff'
                }
              }}
              onClick={() => setStep('buyer')}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <ShoppingCart sx={{ fontSize: 60, color: '#007bff', mb: 2 }} />
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Acheteur
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connectez-vous pour acheter des produits locaux
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Pas encore de compte ?
          </Typography>
          <Button 
            variant="text" 
            onClick={() => {
              handleClose();
              navigate('/register');
            }}
            sx={{ color: '#0066cc', fontWeight: 'medium' }}
          >
            Créer un compte
          </Button>
        </Box>
      </DialogContent>
    </Box>
  );

  const renderLoginForm = (userType) => {
    const isFarmer = userType === 'farmer';
    const title = isFarmer ? 'Connexion Vendeur' : 'Connexion Acheteur';
    const color = isFarmer ? '#28a745' : '#007bff';
    const icon = isFarmer ? <Agriculture /> : <ShoppingCart />;

    return (
      <Box>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 30 } })}
            <Typography variant="h5" component="h2" sx={{ color, fontWeight: 'bold' }}>
              {title}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(userType); }}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                )
              }}
            />
            
            <TextField
              fullWidth
              label="Mot de passe"
              name="password"
              type={formData.showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    name="rememberMe"
                    color="primary"
                  />
                }
                label="Se souvenir de moi"
              />
              
              <Link 
                component="button"
                variant="body2"
                onClick={() => navigate('/reset-password')}
                sx={{ color: '#0066cc' }}
              >
                Mot de passe oublié?
              </Link>
            </Box>
            
            <Button
              variant="contained"
              type="submit"
              fullWidth
              disabled={isLoading}
              sx={{ 
                bgcolor: color,
                '&:hover': { bgcolor: isFarmer ? '#1e7e34' : '#0056b3' },
                py: 1.5,
                mb: 2
              }}
            >
              {isLoading ? <div className="loader"></div> : "Se connecter"}
            </Button>
            
            <Divider sx={{ my: 2 }} />
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setStep('choice')}
              sx={{ color: '#666' }}
            >
              Retour au choix du type de compte
            </Button>
          </Box>
        </DialogContent>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: step === 'choice' ? '400px' : '500px'
        }
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'grey.500'
        }}
      >
        <Close />
      </IconButton>
      
      {step === 'choice' && renderChoiceStep()}
      {step === 'farmer' && renderLoginForm('farmer')}
      {step === 'buyer' && renderLoginForm('buyer')}
    </Dialog>
  );
};

export default LoginModal;